import {AfterViewChecked, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {FormBuilder, FormControl, Validators} from "@angular/forms";
import {MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {environment} from "../../../environments/environment";

// models
import {Country, OnboardingBsa, OnboardingRequest, ValidationDetail} from "../../shared/model/models";
import {ReviewState} from "../../review/models/review-state";

// services
import {Observable, Subject, combineLatest} from 'rxjs';
import {map, startWith, takeUntil, tap} from 'rxjs/operators';
import {FacadeService} from "../../shared/service-facade/facade.service";
import {ApplicationLogDetailsRequest, ApplicationLogStepRequest, ApplicationProductRequest, Log, Step} from "leia-api";
import {NGXLogger} from "ngx-logger";
import {SignupService} from "../service/signup.service";
import {SectionStatusService} from "../../status-tracker/section-status.service";
import {StatusTrackerInterface} from "../../shared/interfaces/status-tracker.interface";
import {UtilitiesService} from 'src/app/shared/services/utilities.service';
import {BSACountryListValidator} from './bsa-country-list-validator';
import {MatChipInputEvent} from '@angular/material/chips';
import {CoreService} from 'src/app/shared/services/core.service';
import {ReviewService} from "../../review/services/review.service";
import {ComponentNameEnum} from "../../shared/model/component-name.enum";
import StateEnum = Step.StateEnum;
import NameEnum = Step.NameEnum;
import OnboardingStepEnum = ApplicationLogStepRequest.OnboardingStepEnum;
import OnboardingStateEnum = ApplicationLogStepRequest.OnboardingStateEnum;
import OnboardingSourceEnum = ApplicationLogDetailsRequest.OnboardingSourceEnum;
import {BSA_DETAILS_KEY} from "../../shared/constants";
import { SecureApplicationReasonEnum } from 'src/app/shared/model/secure-application-reason.enum';
import { ProductType } from '../models/product-type';
import { SessionStorageService } from 'src/app/shared/services/session-storage.service';

@Component({
  selector: 'app-bsa-profile',
  templateUrl: './bsa-profile.component.html',
  styleUrls: ['./bsa-profile.component.scss']
})
export class BsaProfileComponent implements OnInit, AfterViewChecked, StatusTrackerInterface, OnDestroy {
  @Input() reviewState: ReviewState = ReviewState.Initial;
  @Input() hasCoApplicant = false;
  @ViewChild('countryInput') countryInput!: ElementRef;
  @ViewChild('auto') matAutocomplete!: MatAutocomplete;
  @ViewChild('chipList') chipList!: ElementRef | any;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  showInternationalSelect: boolean = false;
  reviewStates = ReviewState;
  shoppingCartOnboardingRequest: OnboardingRequest;
  onboardingBsaProfile: OnboardingBsa;
  onboardingBsaProfileOriginal: OnboardingBsa;

  isSectionComplete: boolean = false;

  // used autocomplete and chip for international country list
  selectable = true;
  removable = true;
  internationalCountryCtrl = new FormControl();
  filteredCountries?: Observable<Country[]>;
  countryList: Country[] = [];
  separatorKeysCodes: number[] = [ENTER, COMMA];
  maxNumberOfCountries: number = 5;
  uneditedFormData: any;

  validationDetail: ValidationDetail;

  destroySubject$: Subject<void> = new Subject();

  selectedInternationalCountry: string[] = [];

  bsaDetails = "";
  isOrderChecks: string = '';
  isOrderDebit: string = '';
  bsaProfileForm = SignupService.createBSAQuestionsForm(
    [this.bsaCountryListValidator.checkCountryListIfNecessary()]
  );

  canViewOrderOptions = this.facadeService.isInAuthenticatedFlow() || this.facadeService.isInJointMaintenanceFlow();

  showOrderDebitCard$ = this.bsaProfileForm.get("useDebitCard")?.valueChanges.pipe(
    map(value => value)
  );

  showOrderChecks$ = this.bsaProfileForm.get("useChecks")?.valueChanges.pipe(
    map(value => value)
  );

  constructor(
              private facadeService: FacadeService,
              private signupService: SignupService,
              private statusTrackerService: SectionStatusService,
              private logger: NGXLogger,
              private bsaCountryListValidator: BSACountryListValidator,
              private utils: UtilitiesService,
              private coreService: CoreService,
              private reviewService: ReviewService,
              private sessionStorage: SessionStorageService,
              private cdr: ChangeDetectorRef) {
    this.shoppingCartOnboardingRequest = new OnboardingRequest();
    this.onboardingBsaProfile = this.shoppingCartOnboardingRequest.bsaInfo;
    this.onboardingBsaProfileOriginal = this.utils.cloneObject(this.onboardingBsaProfile);
    this.validationDetail = new ValidationDetail(this.bsaProfileForm, new Map<string, string>());

    this.facadeService.updateBSAProductProperties$.pipe(takeUntil(this.destroySubject$)).subscribe(async () => {
      const applicationRequest = this.facadeService.getShoppingCartApplicationLogsRequest();
      this._updateProductProperties(applicationRequest.products!);
      this.facadeService.setShoppingCartApplicationLogsRequest(applicationRequest);
      await this.facadeService.updateApplication();
    });
  }
  
  ngAfterViewChecked(): void {
    this.removeAriaPropInterCountry();
  }

  ngOnInit(): void {
    this.bsaDetails = this.facadeService.getStorageItem(BSA_DETAILS_KEY);

    this.logger.debug("Starting bsa profile component...");
    this.populateLists();
    this.setPageDefaults();
    this.setupStatusTracker();

    this.setupValidationMap();
    this.setupValidationTrigger();

    if (this.facadeService.isInAuthenticatedFlow()) {
      this.reviewState = ReviewState.Initial;
    }

    const secureApplicationReason = this.facadeService.getSecuraApplicationReason();
    if (secureApplicationReason && secureApplicationReason === SecureApplicationReasonEnum.resume) {
      this.reviewState = ReviewState.ReadOnly;
    }

    //load application data even on browser back.
    if (this.reviewState === ReviewState.ReadOnly || this.coreService.isManualNavigation) {
      this.loadApplicationData();
    }

    this.signupService.addFormToTrackState(this.bsaProfileForm);

    this.bsaProfileForm.valueChanges.pipe(takeUntil(this.destroySubject$)).subscribe(value => {
      this.signupService.formElementHasChanged = true;
    });
    this.checkProductSetting();
  }

  ngOnDestroy(): void {
    this.destroySubject$.next();
  }

  /**
     * Checks the product settings for ordering checks and debit.
     */
  private checkProductSetting(): void {
    this.isOrderChecks = this.sessionStorage.get("orderCheck")?.toLowerCase();
    this.isOrderDebit = this.sessionStorage.get("orderDebit")?.toLowerCase();
  }

  setupValidationTrigger(): void {
    this.statusTrackerService.onTriggerValidations.subscribe(() => {
      this.internationalCountryCtrl.markAllAsTouched()
      this.bsaProfileForm.markAllAsTouched();
      this.bsaProfileForm.updateValueAndValidity();
    });
  };

  getFormControlValue(name: string) {
    const {value} = this.bsaProfileForm.get(name)!;
    return value;
  }

  getSelectedCountryList(): Country[] {
    const {internationalCountry} = this.bsaProfileForm.value;
    return !Array.isArray(internationalCountry)
      ? (!!internationalCountry ? [internationalCountry] : [])
      : internationalCountry;
  }

  /***
   * Sets up the tracker validation states for this component
   */
  setupStatusTracker(): void {
    this.bsaProfileForm.statusChanges.pipe(takeUntil(this.destroySubject$)).subscribe(validChange => {
        this.updateValidations();

        setTimeout(async () => {
          this.logger.debug("Status Change:" + validChange);

          if (validChange === "VALID") {
            if (this.reviewState === ReviewState.Initial) {
              await this.sectionComplete();               //latest status
            }
          }
        });
      }
    );
  }

  /**
   * Called when this component is finished with entry and ready to send data to service.
   */
  sectionComplete(): Promise<void> {
    let logs: Log[] = [];
    let applicationLogDetailsRequests: ApplicationLogDetailsRequest[] = [];

    if (!this.isSectionComplete) {
      this.isSectionComplete = true;

      let log = new Log();
      log.source = "BB8";
      log.timestamp = new Date().toISOString();
      log.details = "BSA Profile section is complete.";

      logs.push(log);
      applicationLogDetailsRequests.push(this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, "BSA Profile section is complete."));
    }
    return this.updateApplication(StateEnum.Success, OnboardingStateEnum.Success, logs, applicationLogDetailsRequests);
  }

  loadApplicationData(): void {
    const applicationRequest = this.facadeService.getShoppingCartApplicationLogsRequest();
    const {
      achTransactions, wireTransferDomestic, wireTransferInternational,
      wireTransferInternationalCountries, cashTransactions, usingChecks,
      usingDebitCard, orderChecks, orderDebitCard
    } = applicationRequest.products!.find(({productType}) => productType === ProductType.Checking)!;
    const selectedCountries = wireTransferInternationalCountries?.split(',');
    this.bsaProfileForm.patchValue({
      achTransactions,
      wireDomestic: wireTransferDomestic,
      wireInternational: wireTransferInternational,
      internationalCountry: this.countryList.filter(country => selectedCountries?.includes(country.countryName)),
      cashTransactions,
      orderChecks,
      orderDebitCard
    });
    setTimeout(() => {
      this.bsaProfileForm.get("useChecks")?.setValue(usingChecks, { emitEvent: true });
      this.bsaProfileForm.get("useDebitCard")?.setValue(usingDebitCard);
    }, 10);
    this.bsaProfileForm.updateValueAndValidity();
  }

  /**
   * Update leia service with latest info from applicant.
   * @param onboardingRequest Onboarding request tht was saved to shopping cart.
   * @param state State enum, success or failure.
   * @param log Log entry
   */
  updateApplication(state: Step.StateEnum, stateEnum: OnboardingStateEnum, logs: Log[], applicationLogDetailsRequests: ApplicationLogDetailsRequest[]): Promise<void> {
    const applicationRequest = this.facadeService.getShoppingCartApplicationLogsRequest();
    const onboardingRequest = this.facadeService.getShoppingCartOnboardingRequest();

    applicationRequest.step = this.utils.createStep(NameEnum.AccountDetails, state, logs);
    applicationRequest.applicationLogStepRequest = this.utils.createApplicationLogStepRequest(OnboardingStepEnum.AccountDetails, stateEnum, OnboardingSourceEnum.Bb8, applicationLogDetailsRequests);

    this._updateProductProperties(applicationRequest.products!);

    const {
      achTransactions, wireDomestic, wireInternational,
      cashTransactions, useDebitCard, useChecks,
    } = this.bsaProfileForm.value;
    const selectedCountries = this.getSelectedCountryList();

    const onboardingBsaProfile = new OnboardingBsa();
    onboardingBsaProfile.achTransactions = achTransactions;
    onboardingBsaProfile.wireTransferDomestic = wireDomestic;
    onboardingBsaProfile.wireTransferInternational = wireInternational;
    onboardingBsaProfile.internationalCountryList = selectedCountries;
    onboardingBsaProfile.cashTransactions = cashTransactions;
    onboardingBsaProfile.useChecks = useChecks;
    onboardingBsaProfile.useDebitCard = useDebitCard;

    onboardingRequest.bsaInfo = onboardingBsaProfile;

    this.facadeService.setShoppingCartApplicationLogsRequest(applicationRequest);
    this.facadeService.setShoppingCartOnboardingRequest(onboardingRequest);
    return this.facadeService.updateApplication();
  }

  private _updateProductProperties(products: ApplicationProductRequest[]): void {
    const {
      achTransactions, wireDomestic, wireInternational, orderChecks, orderDebitCard,
      cashTransactions, useDebitCard, useChecks,
    } = this.bsaProfileForm.value;
    const selectedCountries = this.getSelectedCountryList();
    const countryList = selectedCountries.map(country => country.countryName).join(',');

    SignupService.updateBSAProperties(
      products!,
      {
        achTransactions, cashTransactions, countryList, orderChecks, orderDebitCard,
        useChecks, useDebitCard, wireDomestic, wireInternational
      },
      this.facadeService.isInAuthenticatedFlow(),
      this.facadeService.isInJointMaintenanceFlow()
    )


  }

  /**
   * Set page defaults
   */
  setPageDefaults(): void {

    if (environment.development && (this.onboardingBsaProfile.cashTransactions == undefined)) {
      this.bsaProfileForm.patchValue({
        achTransactions: undefined,
        wireDomestic: true,
        wireInternational: false,
        cashTransactions: true,
      });
    }
  }

  /**
   * Put the view in edit mode
   */
  populateLists(): void {
    this.getCountryList();

    // Country Auto-complete filter
    this.filteredCountries = this.bsaProfileForm.controls['countryInput'].valueChanges.pipe(
      startWith(null),
      map(id => id ? this._filter(id) : this.countryList.slice(),
      ));
  }

  /***
   *
   * @param value
   * @private
   */
  private _filter(value: string): Country[] {
    // filter the country list - autocompplete

    return this.countryList.filter(option => option.countryName.toLowerCase().indexOf(value) !== -1);
  }

  tokenAdded(event: MatChipInputEvent): void {
    if (!event.chipInput) {
      return;
    }
    event.chipInput.inputElement.value = "";
    setTimeout(() => {
      if(this.chipList && this.chipList?.chips.length < 5) {
        this.countryInput.nativeElement.focus();
      } 
      // this.countryInput.nativeElement.blur();
    }, 0);
  }

  /***
   *
   * @param countryId
   */
  remove(countryId: number): void {
    let country = this.countryList.find(o => o.countryId === countryId);

    if (country !== undefined) {
      const {internationalCountry} = this.bsaProfileForm.controls;
      internationalCountry.value.splice(
        internationalCountry.value.findIndex((value: Country) => country!.countryId === value.countryId),
        1
      );

      internationalCountry.setValue(internationalCountry.value);
      internationalCountry.updateValueAndValidity();

      // Also remove from the selectedInternationalCountry array to update aria attributes
      if (country.countryName && this.selectedInternationalCountry.includes(country.countryName)) {
        const index = this.selectedInternationalCountry.indexOf(country.countryName);
        if (index > -1) {
          const z = this.selectedInternationalCountry.splice(index, 1);
          this.cdr.detectChanges();
        }
      }

      // Update aria-selected attributes in the dropdown options
        const listbox = document.querySelector('div.mat-autocomplete-visible[role="listbox"]');
        if (listbox) {
          const roleOptions = listbox.querySelectorAll('[role="option"]');
          if (roleOptions.length > 0) {
            this.autocompleteTrigger.closePanel();
          }
        }
    }
  }

  /***
   *
   * @param event
   */
  selected(event: MatAutocompleteSelectedEvent | any): void {
    let selectedCountry = this.countryList.find(o => o.countryId === event.option.value.countryId);

    if (selectedCountry !== undefined) {
      const selectedCountryList = this.getSelectedCountryList();
      const {internationalCountry, countryInput} = this.bsaProfileForm.controls;

      if (
        selectedCountryList.indexOf(selectedCountry) < 0 &&
        selectedCountryList.length < this.maxNumberOfCountries
      ) {
        selectedCountryList.push(selectedCountry);

        this.countryInput.nativeElement.value = "";
        internationalCountry.setValue(selectedCountryList);
        internationalCountry.updateValueAndValidity();
      }
      countryInput.setValue(null);
      countryInput.updateValueAndValidity();
    }
    setTimeout(() => {
      if(this.chipList && this.chipList?.chips.length < 5) {
        this.countryInput.nativeElement.focus();
      }
      // this.countryInput.nativeElement.blur();
    }, 0);
  }

  /***
   * get the available country list, currently mocked due to not being available in Falcon
   */
  getCountryList(): void {
    // only show international and non-ofac sanctioned countries
    this.countryList = this.facadeService.getCountries()
      .filter(country =>
        country.isInternational
      );
  }

  /***
   * needs to be implemented once Leia has been updated
   */
  onSubmit() {
    // perform any submission activities here
  }

  /***
   * Put the component in edit mode - Review page
   */
  editDetails() {
    this.uneditedFormData = this.bsaProfileForm.value;
    this.uneditedFormData.internationalCountry = JSON.parse(JSON.stringify(this.uneditedFormData.internationalCountry));
    this.reviewState = ReviewState.Edit;

    this.reviewService.editStateArray.push([ComponentNameEnum.BSAProfile.toString(), 0])
    this.reviewService.editStateMapChanged.emit();
  }

  /**
   * Save the edited fields and set back to readonly
   */
  async save() {
    if (!this.bsaProfileForm.valid) {
      this.bsaProfileForm.markAllAsTouched();
      this.bsaProfileForm.updateValueAndValidity();
      return;
    }

    await this.sectionComplete();
    this.reviewState = ReviewState.ReadOnly;

    this.reviewService.editStateArray.splice(this.reviewService.editStateArray.findIndex(val => val[0] === ComponentNameEnum.BSAProfile.toString() && val[1] === 0), 1)
    this.reviewService.editStateMapChanged.emit();
  }

  /**
   * Cancel edit mode and restore fields to previous values.
   */
  cancel() {
    this.bsaProfileForm.patchValue(this.uneditedFormData, {emitEvent: false});
    this.reviewState = ReviewState.ReadOnly;

    this.reviewService.editStateArray.splice(this.reviewService.editStateArray.findIndex(val => val[0] === ComponentNameEnum.BSAProfile.toString() && val[1] === 0), 1)
    this.reviewService.editStateMapChanged.emit();
  }

  /**
   * Called to see if the form is dirty and has been modified.
   */
  formIsDirty(): boolean {
    if (this.bsaProfileForm.controls['achTransactions']?.dirty ||
      this.bsaProfileForm.controls['wireDomestic']?.dirty ||
      this.bsaProfileForm.controls['wireInternational']?.dirty ||
      this.bsaProfileForm.controls['cashTransactions']?.dirty ||
      this.bsaProfileForm.controls['useDebitCard']?.dirty ||
      this.bsaProfileForm.controls['useChecks']?.dirty
    ) {
      return true;
    }
    return false;
  }

  updateValidations(): void {
    this.signupService.validationsChange.emit(this.validationDetail);
  }

  /**
   * Setup validation map for labels.
   */
  setupValidationMap(): void {
    let labelMap = new Map<string, string>();
    labelMap.set("achTransactions", "ACH Transactions");
    labelMap.set("wireDomestic", "Wire Domestic");
    labelMap.set("wireInternational", "Wire International");
    labelMap.set("internationalCountry", "No International Countries Selected");
    labelMap.set("cashTransactions", "Cash Transactions");
    labelMap.set("useDebitCard", "Use Debit Card");
    labelMap.set("useChecks", "Use Checks");

    this.validationDetail = new ValidationDetail(this.bsaProfileForm, labelMap);
  }

  removeAriaPropInterCountry() {
    const removeAttrMatChipList: NodeListOf<Element> = document.querySelectorAll('mat-chip-list');
    const removeAttrMatChip: any = document.querySelectorAll('mat-chip');
    const addIdAutoComplete: any = document.querySelector('div.mat-autocomplete-visible');
    const addMultiSelectTrue: any = document.querySelector('div.mat-autocomplete-visible[role="listbox"]');
    const removeProp = ['aria-multiselectable', 'aria-orientation', 'aria-disabled', 'aria-invalid', 'tabindex', 'aria-describedby'];
    // mat chip list - aria prop removal
    if(removeAttrMatChipList) {
      removeAttrMatChipList.forEach((prop: Element) => {
        removeProp.forEach((attribute: string) => {
          prop.removeAttribute(attribute);
          prop.setAttribute('role', 'group');
        });
      });
    }
    // div autocomplete - id attribute add
    if(addIdAutoComplete) {
      addIdAutoComplete.setAttribute('id', 'tcb-autocomplete-list');
    }
    // mat chip - role option removal
    if(removeAttrMatChip) {
      removeAttrMatChip.forEach((attr: any) => {
        attr.removeAttribute('role');
        attr.setAttribute('tabindex', '0');
        attr.setAttribute('role', 'button');
        attr.setAttribute('aria-label', `${attr.childNodes[1].textContent.trim()} - remove`);
        
        if(!this.selectedInternationalCountry.includes(attr.childNodes[1].textContent.trim())) {
          this.selectedInternationalCountry.push(attr.childNodes[1].textContent.trim());
        }
      })
    }
    // aria-multiselect - true value add
    if(addMultiSelectTrue) {
      addMultiSelectTrue.setAttribute('aria-multiselectable', 'true');
      // remove tabindex from role=option elements
      const roleOptions = addMultiSelectTrue.querySelectorAll('[role="option"]');
      if (roleOptions.length > 0) {
        roleOptions.forEach((option: any) => {
          option.removeAttribute('tabindex');
          // setting aria-selected to true for selected countries
          if(this.selectedInternationalCountry.includes(option.textContent.trim())) {
            option.setAttribute('aria-selected', 'true');
          } else {
            option.removeAttribute('aria-selected');
          }
        });
      }
    }
  }

  onInputKeydown(event: KeyboardEvent) {
    if ((event.code === 'Space' || event.key === ' ') && this.autocompleteTrigger && this.autocompleteTrigger.activeOption) {
      event.preventDefault();
      // Manually select the active option
      this.selected({ option: this.autocompleteTrigger.activeOption });
    }
  }

  onChipKeydown(event: KeyboardEvent, countryId: number): void {
    if (event.code === 'Enter' || event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.remove(countryId);
      return;
    }

    if (event.code === 'Space' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.remove(countryId);
      return;
    }
  }
  
}
